/**
 * IMPORT CSV - ÉTAPE 2 : MAPPING
 * Interface de correspondance colonnes CSV → champs BDD
 */

import { Badge, Select } from '../ui';
import type { CSVColumnMapping, CSVImportConfig } from '../../types/csv';

interface ImportStepMappingProps<T> {
  mapping: CSVColumnMapping[];
  config: CSVImportConfig<T>;
  onMappingChange: (mapping: CSVColumnMapping[]) => void;
  csvData: { headers: string[]; rows: Record<string, string>[] };
}

export function ImportStepMapping<T>({
  mapping,
  config,
  onMappingChange,
  csvData,
}: ImportStepMappingProps<T>) {
  const allFields = [...config.requiredFields, ...config.optionalFields];

  const handleFieldChange = (csvColumn: string, dbField: string | null) => {
    const newMapping = mapping.map((m) => {
      if (m.csvColumn === csvColumn) {
        return {
          ...m,
          dbField,
          isRequired: dbField ? config.requiredFields.includes(dbField as keyof T) : false,
        };
      }
      return m;
    });
    onMappingChange(newMapping);
  };

  const getFieldLabel = (field: keyof T | string): string => {
    return config.fieldLabels[field as keyof T] || String(field);
  };

  // Compter les champs obligatoires mappés
  const requiredMapped = mapping.filter(
    (m) => m.dbField && config.requiredFields.includes(m.dbField as keyof T)
  ).length;

  const requiredTotal = config.requiredFields.length;

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h3 className="text-2xl font-semibold mb-3">Correspondance des colonnes</h3>
        <p className="text-base text-gray-600">
          Associez chaque colonne du CSV à un champ de la base de données
        </p>
        <div className="mt-2">
          <Badge variant={requiredMapped === requiredTotal ? 'default' : 'destructive'}>
            {requiredMapped} / {requiredTotal} champs obligatoires mappés
          </Badge>
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-6 py-4 border-b">
          <div className="grid grid-cols-3 gap-6 text-sm font-medium text-gray-700">
            <div>Colonne CSV</div>
            <div>Champ de la base</div>
            <div>Aperçu des données</div>
          </div>
        </div>

        <div className="divide-y max-h-[600px] overflow-y-auto">
          {mapping.map((map, index) => {
            // Aperçu des 3 premières valeurs
            const previewValues = csvData.rows
              .slice(0, 3)
              .map((row) => row[map.csvColumn])
              .filter(Boolean);

            return (
              <div
                key={`${map.csvColumn}-${index}`}
                className="px-6 py-4 grid grid-cols-3 gap-6 items-start hover:bg-gray-50"
              >
                {/* Colonne CSV */}
                <div className="flex flex-col space-y-1">
                  <span className="text-base font-medium text-gray-900">{map.csvColumn}</span>
                  {map.dbField &&
                    map.suggestions.length > 0 &&
                    map.suggestions[0] === map.dbField && (
                      <Badge variant="default" className="text-xs w-fit">
                        Suggestion
                      </Badge>
                    )}
                </div>

                {/* Sélecteur de champ */}
                <div className="space-y-1">
                  <Select
                    value={map.dbField || 'ignore'}
                    onChange={(e) =>
                      handleFieldChange(
                        map.csvColumn,
                        e.target.value === 'ignore' ? null : e.target.value
                      )
                    }
                    className="w-full"
                  >
                    <option value="ignore" className="text-gray-500">
                      Ignorer cette colonne
                    </option>

                    {/* Afficher les suggestions en premier si disponibles */}
                    {map.suggestions.length > 0 && (
                      <>
                        <optgroup label="✨ Suggestions">
                          {map.suggestions.map((field) => {
                            const label = getFieldLabel(field);
                            const isRequired = config.requiredFields.includes(field as keyof T);
                            return (
                              <option key={`suggestion-${field}`} value={field}>
                                {label} {isRequired ? '(Requis)' : ''}
                              </option>
                            );
                          })}
                        </optgroup>
                        <optgroup label="──────────" disabled></optgroup>
                      </>
                    )}

                    {/* Afficher tous les champs triés alphabétiquement */}
                    <optgroup label="Tous les champs">
                      {allFields
                        .map((field) => ({
                          field,
                          fieldStr: String(field),
                          label: getFieldLabel(field),
                          isRequired: config.requiredFields.includes(field),
                        }))
                        .sort((a, b) => a.label.localeCompare(b.label))
                        .map(({ fieldStr, label, isRequired }) => (
                          <option key={`all-${fieldStr}`} value={fieldStr}>
                            {label} {isRequired ? '(Requis)' : ''}
                          </option>
                        ))}
                    </optgroup>
                  </Select>

                  {/* Indicateur de champ obligatoire */}
                  {map.isRequired && (
                    <span className="text-sm text-red-600">* Champ obligatoire</span>
                  )}
                </div>

                {/* Aperçu */}
                <div className="text-sm text-gray-600 space-y-1">
                  {previewValues.length > 0 ? (
                    previewValues.map((value, i) => (
                      <div key={i} className="truncate" title={value}>
                        {value}
                      </div>
                    ))
                  ) : (
                    <div className="text-gray-400 italic">Aucune donnée</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Liste des champs obligatoires non mappés */}
      {requiredMapped < requiredTotal && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <p className="text-base font-medium text-red-800 mb-3">Champs obligatoires manquants :</p>
          <ul className="list-disc list-inside text-base text-red-700 space-y-1">
            {config.requiredFields
              .filter((field) => !mapping.some((m) => m.dbField === String(field)))
              .map((field) => (
                <li key={String(field)}>{getFieldLabel(field)}</li>
              ))}
          </ul>
        </div>
      )}
    </div>
  );
}
